import { t } from '../../shared/config/translations'
import { clearNativeSessionToken } from '../auth/nativeSessionToken'
import { clearAllSettingsCache } from '../cache/settings'
import { closeAllDialogs } from '../components/Dialog'
import { apiUrl, IS_NATIVE_APP } from '../config/environment'
import { disposeProtectedSession } from '../router'
import { onAppForegroundChange } from '../utils/appLifecycle'
import { applyTheme } from '../utils/theme'
import { authenticatedFetch } from './authenticatedFetch'
import { closeAppEvents, initAppEvents } from './events'
import { abortSessionRequests } from './requestSession'
import { clearUserCache } from './user'

const AUTH_NOTICE_KEY = 'auth-notice'
const AUTH_REVALIDATE_THROTTLE_MS = 15_000
const AUTH_REVALIDATE_INTERVAL_MS = 5 * 60_000

let authRedirectInFlight = false
let authLifecycleConfigured = false
let lastAuthRevalidationAt = 0

/**
 * Clears all client-side auth-related caches.
 */
export function resetAuthState(signedOut = false): void {
	abortSessionRequests()
	disposeProtectedSession()
	clearUserCache(signedOut)
	clearAllSettingsCache()
}

/**
 * Clears local auth state, then logs out on the server.
 */
export async function logout(): Promise<void> {
	if (IS_NATIVE_APP) {
		try {
			const response = await authenticatedFetch(
				apiUrl('/auth/native/session'),
				{ method: 'DELETE' },
			)
			if (!response.ok && response.status !== 401) {
				console.error(
					`Native session revocation failed with ${response.status}.`,
				)
			}
		} catch (error) {
			console.error('Native session revocation failed:', error)
		} finally {
			await clearAuthAndNavigateToLogin()
		}
		return
	}
	closeAppEvents()
	try {
		const response = await fetch('/auth/logout', { method: 'POST' })
		if (!response.ok) {
			throw new Error(`Logout failed with ${response.status}.`)
		}
	} catch (error) {
		initAppEvents()
		throw error
	}
	await clearAuthAndNavigateToLogin()
}

/** Clears all local auth state and renders the login route. */
export async function clearAuthAndNavigateToLogin(): Promise<void> {
	closeAppEvents()
	closeAllDialogs()
	await clearNativeSessionToken()
	resetAuthState(true)
	const { navigateTo } = await import('../router')
	history.replaceState(null, '', '/login')
	await navigateTo('/login')
}

/**
 * Stores a one-time auth notice for the next route render.
 */
function setAuthNotice(message: string): void {
	sessionStorage.setItem(AUTH_NOTICE_KEY, message)
}

/**
 * Reads and clears the pending auth notice, if present.
 */
export function consumeAuthNotice(): string | null {
	const message = sessionStorage.getItem(AUTH_NOTICE_KEY)
	if (!message) return null
	sessionStorage.removeItem(AUTH_NOTICE_KEY)
	return message
}

/**
 * Resets local auth state and routes the user back to login after auth failure.
 */
export async function handleAuthFailure(
	reason: 'session-expired' | 'refresh-failed' = 'session-expired',
): Promise<void> {
	if (authRedirectInFlight) return

	authRedirectInFlight = true
	setAuthNotice(
		reason === 'refresh-failed'
			? t('auth.sessionRefreshFailed')
			: t('auth.sessionExpired'),
	)

	try {
		await clearAuthAndNavigateToLogin()
	} finally {
		authRedirectInFlight = false
	}
}

/** Revalidates auth on resume and periodically while the app stays open. */
export function setupAuthLifecycle(): void {
	if (authLifecycleConfigured) return
	authLifecycleConfigured = true

	const revalidate = () => {
		if (document.visibilityState === 'hidden') return
		void revalidateAuthState()
	}

	onAppForegroundChange((active) => {
		if (active) revalidate()
	})
	window.setInterval(revalidate, AUTH_REVALIDATE_INTERVAL_MS)
}

/** Re-checks server auth state, throttled to avoid repeated probes. */
async function revalidateAuthState(): Promise<void> {
	if (authRedirectInFlight) return
	const { getUser, hasAuthenticatedUser } = await import('./user')
	if (!hasAuthenticatedUser()) return

	const now = Date.now()
	if (now - lastAuthRevalidationAt < AUTH_REVALIDATE_THROTTLE_MS) return
	lastAuthRevalidationAt = now

	const user = await getUser({ force: true })
	if (user) {
		const { flushPendingSettings } = await import('./settings')
		void flushPendingSettings()
	}
	applyTheme(user?.settings?.theme === 'light')

	if (!user && window.location.pathname !== '/login') {
		await handleAuthFailure('session-expired')
	}
}
